/**
 * workday_config.repository.ts — 업무일/휴일 조직 정책 (조직 공통).
 *
 * Firestore: "system_settings/workday_config" — { weeklyOff, holidays, updated_at }
 *   - 관리자만 편집(system_settings 기본 write=isAdmin), 모든 활성 사용자 읽기
 *   - rules 변경 불필요: workday_config 는 system_settings/{key} 기본 분기(else→isAdmin) 적용
 *
 * 저장 시 sanitize 로 요일 범위·날짜 형식·종류 화이트리스트를 강제한다.
 * (천문연 API 자동 불러오기 CF 도 같은 sanitize 규칙으로 holidays 를 merge 예정)
 */
import { doc, onSnapshot, setDoc, Timestamp } from "firebase/firestore";
import { db } from "./firebase";
import { tenantSettingsDocPath } from "@/domain/tenantPath";
import {
  DEFAULT_WORKDAY_CONFIG,
  HOLIDAY_KINDS,
  type Holiday,
  type HolidayKind,
  type WorkdayConfig,
} from "@/domain/workday";

const DOC_PATH = ["system_settings", "workday_config"] as const;

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function sanitizeWeeklyOff(v: unknown): number[] {
  if (!Array.isArray(v)) return [...DEFAULT_WORKDAY_CONFIG.weeklyOff];
  const set = new Set<number>();
  for (const x of v) {
    const n = Math.round(Number(x));
    if (Number.isFinite(n) && n >= 0 && n <= 6) set.add(n);
  }
  return [...set].sort((a, b) => a - b);
}

function sanitizeHolidays(v: unknown): Holiday[] {
  if (!Array.isArray(v)) return [];
  const map = new Map<string, Holiday>(); // 날짜 중복 시 마지막 우선
  for (const raw of v) {
    const r = asRecord(raw);
    const date = typeof r.date === "string" ? r.date : "";
    if (!YMD_RE.test(date)) continue;
    const kind: HolidayKind = HOLIDAY_KINDS.includes(r.kind as HolidayKind)
      ? (r.kind as HolidayKind)
      : "custom";
    map.set(date, {
      date,
      name: typeof r.name === "string" ? r.name : "",
      kind,
      source: r.source === "api" ? "api" : "manual",
    });
  }
  return [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
}

export function sanitizeWorkdayConfig(data: unknown): WorkdayConfig {
  const r = asRecord(data);
  return {
    weeklyOff: sanitizeWeeklyOff(r.weeklyOff),
    holidays: sanitizeHolidays(r.holidays),
  };
}

export const workdayConfigRepo = {
  watch(cb: (c: WorkdayConfig) => void, onError?: (e: Error) => void): () => void {
    return onSnapshot(
      doc(db, tenantSettingsDocPath(DOC_PATH[1])),
      (snap) => cb(snap.exists() ? sanitizeWorkdayConfig(snap.data()) : DEFAULT_WORKDAY_CONFIG),
      onError,
    );
  },

  async save(config: WorkdayConfig): Promise<void> {
    const clean = sanitizeWorkdayConfig(config);
    await setDoc(
      doc(db, tenantSettingsDocPath(DOC_PATH[1])),
      { ...clean, updated_at: Timestamp.now() },
      { merge: true },
    );
  },
};
